import React from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { Phone, Mail, Pencil, Trash2 } from 'lucide-react';
import { ContactoForm } from '../hooks/useNuevoCliente';

interface ContactCardProps {
  contacto: ContactoForm;
  onEditar: (contacto: ContactoForm) => void;
  onEliminar: (id: string) => void;
}

function getIniciales(nombres: string): string {
  const palabras = nombres.trim().split(/\s+/).slice(0, 2);
  return palabras.map((p) => p[0] ?? '').join('').toUpperCase();
}

export function ContactCard({ contacto, onEditar, onEliminar }: ContactCardProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '72px 1fr auto auto',
        alignItems: 'center',
        gap: '12px',
        p: '12px 16px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: '#fff',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          borderColor: 'primary.300',
          backgroundColor: 'primary.50',
        },
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          backgroundColor: '#963781',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem', fontFamily: 'Poppins, sans-serif' }}>
          {getIniciales(contacto.nombres)}
        </Typography>
      </Box>

      {/* Datos */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              fontFamily: 'Poppins, sans-serif',
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {contacto.nombres}
          </Typography>
          {contacto.esPrincipal && (
            <Chip
              label="Principal"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: '#963781',
                color: '#fff',
                fontFamily: 'Poppins, sans-serif',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
        </Box>
        {contacto.cargo && (
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.5, fontFamily: 'Poppins, sans-serif' }}>
            {contacto.cargo}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {contacto.telefono && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Phone size={13} color="#666" />
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontFamily: 'Poppins, sans-serif' }}>
                {contacto.telefono}
              </Typography>
            </Box>
          )}
          {contacto.correoElectronico && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Mail size={13} color="#666" />
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  color: 'text.secondary',
                  fontFamily: 'Poppins, sans-serif',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {contacto.correoElectronico}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Botón editar */}
      <IconButton
        size="small"
        onClick={() => onEditar(contacto)}
        aria-label={`Editar contacto ${contacto.nombres}`}
        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
      >
        <Pencil size={16} />
      </IconButton>

      {/* Botón eliminar */}
      <IconButton
        size="small"
        onClick={() => onEliminar(contacto.id)}
        aria-label={`Eliminar contacto ${contacto.nombres}`}
        sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
      >
        <Trash2 size={16} />
      </IconButton>
    </Box>
  );
}
